/**
 * DetailActions — 3 action buttons at the bottom of Worksheet Detail.
 *
 * Per doc 07 §3: "1-tap print CTA". Kirim WA dihapus (2026-08-19) —
 * terlalu banyak aksi membingungkan user; cetak + simpan PDF sudah cukup.
 *
 * Actions:
 *  - Cetak        → onCetak (Phase 4.4: expo-print / window.open)
 *  - Simpan PDF   → onSimpanPdf (4.4)
 *  - Selesai      → onSelesai (REAL: POST /v1/worksheet/:id/complete)
 *
 * The "Selesai" button changes label + style when the worksheet is
 * already marked complete (idempotent UX).
 */

import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Brand, Radii, Spacing } from '@/constants/theme';

interface DetailActionsProps {
  /** Selesai handler — wired in 4.3 to /v1/worksheet/:id/complete */
  onSelesai: () => void;
  /** Cetak handler — wired in 4.4 */
  onCetak: () => void;
  /** Simpan PDF handler — wired in 4.4 */
  onSimpanPdf: () => void;
  /** When true, Selesai button shows "Sudah Selesai" disabled state */
  alreadyCompleted: boolean;
  /** Disable Selesai button while a request is in flight */
  completing: boolean;
  /** Disable print/download while either action is in flight. */
  actionInProgress: 'print' | 'pdf' | null;
}

export function DetailActions({
  onSelesai,
  onCetak,
  onSimpanPdf,
  alreadyCompleted,
  completing,
  actionInProgress,
}: DetailActionsProps) {
  return (
    <View style={styles.container}>
      <View style={styles.primaryRow}>
        <Pressable
          onPress={onCetak}
          disabled={actionInProgress !== null}
          style={({ pressed }) => [
            styles.btnPrimary,
            pressed && styles.btnPressed,
          ]}
          accessibilityRole="button"
          accessibilityLabel="Cetak worksheet"
          accessibilityState={{ disabled: actionInProgress !== null }}
        >
          <Text style={styles.btnPrimaryText}>
            {actionInProgress === 'print' ? 'Mencetak…' : 'Cetak'}
          </Text>
        </Pressable>
      </View>
      <View style={styles.secondaryRow}>
        <Pressable
          onPress={onSimpanPdf}
          disabled={actionInProgress !== null}
          style={({ pressed }) => [
            styles.btnSecondary,
            pressed && styles.btnPressed,
          ]}
          accessibilityRole="button"
          accessibilityLabel="Simpan sebagai PDF"
          accessibilityState={{ disabled: actionInProgress !== null }}
        >
          <Text style={styles.btnSecondaryText}>
            {actionInProgress === 'pdf' ? 'Menyimpan…' : 'Simpan PDF'}
          </Text>
        </Pressable>
      </View>
      <Text style={styles.completionHint}>
        {alreadyCompleted
          ? 'Worksheet ini sudah tercatat selesai di Aktivitas.'
          : 'Setelah selesai dikerjakan, tandai di sini agar tercatat di Aktivitas.'}
      </Text>
      <Pressable
        onPress={onSelesai}
        disabled={alreadyCompleted || completing}
        style={({ pressed }) => [
          styles.btnSelesai,
          alreadyCompleted && styles.btnSelesaiDone,
          pressed && !alreadyCompleted && styles.btnPressed,
        ]}
        accessibilityRole="button"
        accessibilityState={{ disabled: alreadyCompleted || completing }}
        accessibilityLabel={
          alreadyCompleted ? 'Worksheet sudah ditandai selesai' : 'Tandai worksheet selesai'
        }
        accessibilityHint={
          alreadyCompleted
            ? 'Status selesai sudah tercatat di Aktivitas.'
            : 'Mencatat worksheet ini sebagai selesai di Aktivitas.'
        }
      >
        <Text
          style={[
            styles.btnSelesaiText,
            alreadyCompleted && styles.btnSelesaiTextDone,
          ]}
        >
          {completing
            ? 'Menyimpan…'
            : alreadyCompleted
              ? '✓ Sudah Selesai'
              : '✓ Tandai Selesai'}
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.four,
    gap: Spacing.two,
  },
  primaryRow: {
    flexDirection: 'row',
  },
  secondaryRow: {
    flexDirection: 'row',
    gap: Spacing.two,
  },
  completionHint: {
    color: '#6b6573',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
  },
  btnPrimary: {
    flex: 1,
    backgroundColor: Brand.primary,
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnPrimaryText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '700',
  },
  btnSecondary: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    paddingVertical: 12,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSecondaryText: {
    color: Brand.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  btnSelesai: {
    backgroundColor: '#4caf50',
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSelesaiDone: {
    backgroundColor: '#E8F5E9',
    borderWidth: 1,
    borderColor: '#4caf50',
  },
  btnSelesaiText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '700',
  },
  btnSelesaiTextDone: {
    color: '#4caf50',
  },
  btnPressed: {
    opacity: 0.7,
  },
});
